import { CountDecimals, RoundAwayFromZero } from 'src/utils/math';
import { SpecificationParameterDto } from '../specifications/specification';
import { ProductParameterDetailDto } from './productParameter';

const isNumber = (value: number | null | undefined): value is number =>
  value !== null && value !== undefined;

export const getCorrectedValue = (parameter: ProductParameterDetailDto) => {
  const { measuredValue, correctionPart } = parameter;
  const numberOfDecimalsMeasuredValue = measuredValue
    ? CountDecimals(measuredValue)
    : 0;
  const numberOfDecimalsCorrectionPart = correctionPart
    ? CountDecimals(correctionPart)
    : 0;
  const numberOfDecimals =
    numberOfDecimalsMeasuredValue > numberOfDecimalsCorrectionPart
      ? numberOfDecimalsMeasuredValue
      : numberOfDecimalsCorrectionPart;
  const correctedValue = isNumber(measuredValue)
    ? RoundAwayFromZero(
        measuredValue + (correctionPart ?? 0),
        numberOfDecimals ?? 0
      )
    : null;
  return correctedValue;
};

export const getCorrectedValueAtIndex = (
  parameters: ProductParameterDetailDto[],
  index: number
) => {
  return parameters.length > index
    ? parameters[index].correctedValue ?? null
    : null;
};

export const getCorrectedValueAtIndexFormatted = (
  parameters: ProductParameterDetailDto[],
  index: number
) => {
  const value = getCorrectedValueAtIndex(parameters, index);
  return value !== null ? value.toFixed(3) : null;
};

export const getParameterValue = (
  parameters: ProductParameterDetailDto[],
  sp: SpecificationParameterDto
) => {
  const parameter = parameters.find(
    x => x.parameterId === sp.parameterId && x.methodId === sp.methodId
  );

  const { measuredValue, correctionPart = 0 } = parameter ?? {};
  return isNumber(measuredValue) ? measuredValue + correctionPart : null;
};

export const isMinProductionOk = (
  parameter: ProductParameterDetailDto,
  value?: Nullable<number>
) => {
  const correctedValue = value ?? getCorrectedValue(parameter);
  const minProd = parameter.minProd;
  const decimals = parameter.decimalProd ?? 0;
  if (!isNumber(correctedValue) || !isNumber(minProd)) return true;
  return (
    RoundAwayFromZero(correctedValue, decimals) >=
    RoundAwayFromZero(minProd, decimals)
  );
};

export const isMaxProductionOk = (
  parameter: ProductParameterDetailDto,
  value?: Nullable<number>
) => {
  const correctedValue = value ?? getCorrectedValue(parameter);
  const maxProd = parameter.maxProd;
  const decimals = parameter.decimalProd ?? 0;
  if (!isNumber(correctedValue) || !isNumber(maxProd)) return true;
  return (
    RoundAwayFromZero(correctedValue, decimals) <=
    RoundAwayFromZero(maxProd, decimals)
  );
};

export const isMinCustomerOk = (
  parameter: ProductParameterDetailDto,
  value?: Nullable<number>
) => {
  const correctedValue = value ?? getCorrectedValue(parameter);
  const minCustomer = parameter.minCustomer;
  const decimals = parameter.decimalCustomer ?? 0;
  if (!isNumber(correctedValue) || !isNumber(minCustomer)) return true;
  return (
    RoundAwayFromZero(correctedValue, decimals) >=
    RoundAwayFromZero(minCustomer, decimals)
  );
};

export const isMaxCustomerOk = (
  parameter: ProductParameterDetailDto,
  value?: Nullable<number>
) => {
  const correctedValue = value ?? getCorrectedValue(parameter);
  const maxCustomer = parameter.maxCustomer;
  const decimals = parameter.decimalCustomer ?? 0;
  if (!isNumber(correctedValue) || !isNumber(maxCustomer)) return true;
  return (
    RoundAwayFromZero(correctedValue, decimals) <=
    RoundAwayFromZero(maxCustomer, decimals)
  );
};

export const isParameterProductionOk = (
  value: Nullable<number>,
  parameter: ProductParameterDetailDto
) => isMinProductionOk(parameter, value) && isMaxProductionOk(parameter, value);

export const isParameterCustomerOk = (
  value: Nullable<number>,
  parameter: ProductParameterDetailDto
) => isMinCustomerOk(parameter, value) && isMaxCustomerOk(parameter, value);

export const isParameterOk = (
  value: Nullable<number>,
  parameter: ProductParameterDetailDto
) =>
  isParameterProductionOk(value, parameter) &&
  isParameterCustomerOk(value, parameter);

export const isParameterAdded = (
  parameters: ProductParameterDetailDto[],
  sp: SpecificationParameterDto
) => {
  return parameters.some(
    x => x.parameterId === sp.parameterId && x.methodId === sp.methodId
  );
};
